import { inject, Injectable } from "@angular/core";
import { Actions, createEffect, ofType } from "@ngrx/effects";
import { catchError, mergeMap, tap, concatMap, withLatestFrom, switchMap, map, distinctUntilChanged, filter } from "rxjs/operators";
import { of } from "rxjs";
import { AttioClient, QuoteClient, QuoteSalesCreditClient, DeliveryAddressClient, PdfClient, Operation, CatalogClient } from "src/api-service";
import { Store } from "@ngrx/store";
import { Router } from "@angular/router";
import { TranslateService } from "@ngx-translate/core";

import { selectSelectedCompany } from "src/app/store/user/user.selectors";
import { QuoteActions } from "./quote.actions";
import { selectQuoteId, selectQuoteContractSectionId, selectIsSubscriptionQuote } from "./quote.selectors";
import { selectVersion } from "./version/version.selectors";
import { normalizeError } from "../../app-store-error.types";

@Injectable()
export class QuoteEffects {

    // =========================================================================
    // DEPENDENCY INJECTION
    // =========================================================================
    private readonly actions$ = inject(Actions);
    private readonly store = inject(Store);
    private readonly quoteClient = inject(QuoteClient);
    private readonly quoteSalesCreditClient = inject(QuoteSalesCreditClient);
    private readonly deliveryAddressClient = inject(DeliveryAddressClient);
    private readonly pdfClient = inject(PdfClient);
    private readonly catalogClient = inject(CatalogClient);
    private readonly attioClient = inject(AttioClient);
    private readonly router = inject(Router);
    private readonly translateService = inject(TranslateService);

    // =========================================================================
    // QUOTE CRUD - Initialize, Update, Create
    // =========================================================================

    initializeQuote$ = createEffect(() =>
        this.actions$.pipe(
            ofType(QuoteActions.initializeQuote),
            withLatestFrom(this.store.select(selectSelectedCompany)),
            concatMap(([{ quoteId }, selectedCompany]) =>
                this.quoteClient.updateQuoteInfo(quoteId, selectedCompany.id).pipe(
                    mergeMap(quote => [
                        QuoteActions.initializeQuoteSuccess({
                            quote,
                            toastKey: 'QUOTE.TOAST.INITIALIZE_SUCCESS'
                        })
                    ]),
                    catchError(error => of(QuoteActions.initializeQuoteFailure({
                        error: normalizeError(error),
                        toastKey: 'QUOTE.TOAST.INITIALIZE_ERROR'
                    }))),
                ),
            ),
        ),
    );

    updateQuote$ = createEffect(() =>
        this.actions$.pipe(
            ofType(QuoteActions.update),
            withLatestFrom(this.store.select(selectSelectedCompany)),
            switchMap(([action, selectedCompany]) => {
                const { quoteId, request } = action;
                const toastKey = action.toastKey ?? 'QUOTE.TOAST.UPDATE_SUCCESS';
                return this.quoteClient.patch(quoteId, request, selectedCompany.id).pipe(
                    mergeMap(quote => [
                        QuoteActions.updateSuccess({ quote, toastKey }),
                    ]),
                    catchError(error => of(QuoteActions.updateFailure({
                        error: normalizeError(error),
                        toastKey: toastKey.replace('SUCCESS', 'ERROR'),
                    }))),
                );
            }),
        ),
    );

    // =========================================================================
    // STATUS UPDATES - Under Review, Ready For Review, Back To Sent, Mark As Lost
    // =========================================================================

    updateStatusToUnderReview$ = createEffect(() =>
        this.actions$.pipe(
            ofType(QuoteActions.updateToUnderReview),
            withLatestFrom(this.store.select(selectSelectedCompany)),
            switchMap(([{ quoteId, request, dialogKey }, selectedCompany]) => {
                const companyId = selectedCompany?.id;
                if (!companyId) {
                    return of(QuoteActions.updateToUnderReviewFailure({
                        error: { message: "No company selected" },
                        dialogKey,
                    }));
                }
                return this.quoteClient.setQuoteUnderReview(quoteId, request, companyId).pipe(
                    mergeMap(quote => [QuoteActions.updateToUnderReviewSuccess({
                        quote,
                        dialogKey,
                    })]),
                    catchError(error => of(QuoteActions.updateToUnderReviewFailure({
                        error: normalizeError(error),
                        dialogKey,
                    }))),
                );
            }),
        ),
    );

    updateStatusToReadyForReview$ = createEffect(() =>
        this.actions$.pipe(
            ofType(QuoteActions.updateToReadyForReview),
            withLatestFrom(this.store.select(selectSelectedCompany)),
            switchMap(([{ quoteId, request, dialogKey }, selectedCompany]) => {
                const companyId = selectedCompany?.id;
                if (!companyId) {
                    return of(QuoteActions.updateToReadyForReviewFailure({
                        error: { message: "No company selected" },
                        dialogKey,
                    }));
                }
                return this.quoteClient.setQuoteReadyForReview(quoteId, request, companyId).pipe(
                    mergeMap(quote => [QuoteActions.updateToReadyForReviewSuccess({
                        quote,
                        dialogKey,
                    })]),
                    catchError(error => of(QuoteActions.updateToReadyForReviewFailure({
                        error: normalizeError(error),
                        dialogKey,
                    }))),
                );
            }),
        ),
    );

    updateStatusBackToSent$ = createEffect(() =>
        this.actions$.pipe(
            ofType(QuoteActions.updateBackToSent),
            withLatestFrom(this.store.select(selectSelectedCompany)),
            switchMap(([{ quoteId, request, dialogKey }, selectedCompany]) => {
                const companyId = selectedCompany?.id;
                if (!companyId) {
                    return of(QuoteActions.updateBackToSentFailure({
                        error: { message: "No company selected" },
                        dialogKey,
                    }));
                }
                return this.quoteClient.revertQuoteBackToSent(quoteId, request, companyId).pipe(
                    mergeMap(quote => [QuoteActions.updateBackToSentSuccess({
                        quote,
                        dialogKey,
                    })]),
                    catchError(error => of(QuoteActions.updateBackToSentFailure({
                        error: normalizeError(error),
                        dialogKey,
                    }))),
                );
            }),
        ),
    );

    updateStatusMarkAsLost$ = createEffect(() =>
        this.actions$.pipe(
            ofType(QuoteActions.updateMarkAsLost),
            withLatestFrom(this.store.select(selectSelectedCompany)),
            switchMap(([{ quoteId, request, dialogKey }, selectedCompany]) => {
                const companyId = selectedCompany?.id;
                if (!companyId) {
                    return of(QuoteActions.updateMarkAsLostFailure({
                        error: { message: "No company selected" },
                        dialogKey,
                    }));
                }
                return this.quoteClient.markQuoteAsLost(quoteId, request, companyId).pipe(
                    mergeMap(quote => [QuoteActions.updateMarkAsLostSuccess({
                        quote,
                        dialogKey,
                    })]),
                    catchError(error => of(QuoteActions.updateMarkAsLostFailure({
                        error: normalizeError(error),
                        dialogKey,
                    }))),
                );
            }),
        ),
    );

    extendValidity$ = createEffect(() =>
        this.actions$.pipe(
            ofType(QuoteActions.extendValidity),
            withLatestFrom(this.store.select(selectSelectedCompany)),
            switchMap(([{ quoteId, dialogKey }, selectedCompany]) => {
                const validTo = new Date(Date.UTC(
                    new Date().getFullYear(),
                    new Date().getMonth(),
                    new Date().getDate() + 14
                ));
                const operations: Operation[] = [
                    { op: 'replace', path: '/salesInfo/validTo', value: validTo }
                ];
                return this.quoteClient.patch(quoteId, operations, selectedCompany?.id ?? '').pipe(
                    mergeMap(quote => [QuoteActions.extendValiditySuccess({ quote, dialogKey })]),
                    catchError(error => of(QuoteActions.extendValidityFailure({ error: normalizeError(error), dialogKey }))),
                );
            }),
        ),
    );

    // =========================================================================
    // QUOTE ACTIONS - Send, Preview, Duplicate, Change Customer, Create Order
    // =========================================================================

    sendQuote$ = createEffect(() =>
        this.actions$.pipe(
            ofType(QuoteActions.send),
            withLatestFrom(this.store.select(selectSelectedCompany)),
            switchMap(([{ request, dialogKey, sendToSelf }, selectedCompany]) =>
                this.quoteClient.sendQuote(
                    request.quoteId,
                    selectedCompany?.id ?? '',
                    request.versionId,
                    request.recipientEmail,
                    request.ccEmails,
                    request.subject,
                    request.message,
                    request.senderEmail,
                    request.senderName,
                    request.language,
                    request.quoteType,
                    request.attachTerms,
                    undefined,
                    request.attachmentFiles.map(file => ({ data: file, fileName: file.name })),
                ).pipe(
                    mergeMap(quote => [QuoteActions.sendSuccess({
                        quote,
                        dialogKey,
                        sendToSelf,
                    })]),
                    catchError(error => of(QuoteActions.sendFailure({
                        error: normalizeError(error),
                        dialogKey,
                    }))),
                ),
            ),
        ),
    );

    duplicateQuote$ = createEffect(() =>
        this.actions$.pipe(
            ofType(QuoteActions.duplicateQuote),
            withLatestFrom(this.store.select(selectSelectedCompany)),
            concatMap(([{ quoteId, request, dialogKey }, selectedCompany]) =>
                this.quoteClient.duplicateQuote(quoteId, request, selectedCompany.id).pipe(
                    mergeMap(quote => [
                        QuoteActions.duplicateQuoteSuccess({
                            quote,
                            dialogKey,
                        })
                    ]),
                    catchError(error => of(QuoteActions.duplicateQuoteFailure({
                        error: normalizeError(error),
                        dialogKey,
                    }))),
                ),
            ),
        ),
    );

    changeCustomer$ = createEffect(() =>
        this.actions$.pipe(
            ofType(QuoteActions.changeCustomer),
            withLatestFrom(this.store.select(selectSelectedCompany)),
            concatMap(([{ quoteId, request }, selectedCompany]) =>
                this.quoteClient.patch(quoteId, request, selectedCompany.id).pipe(
                    mergeMap(quote => [
                        QuoteActions.changeCustomerSuccess({
                            quote,
                            toastKey: 'QUOTE.TOAST.CHANGE_CUSTOMER_SUCCESS'
                        })
                    ]),
                    catchError(error => of(QuoteActions.changeCustomerFailure({
                        error: normalizeError(error),
                        toastKey: 'QUOTE.TOAST.CHANGE_CUSTOMER_ERROR'
                    }))),
                ),
            ),
        ),
    );

    createOrder$ = createEffect(() =>
        this.actions$.pipe(
            ofType(QuoteActions.createOrder),
            withLatestFrom(this.store.select(selectSelectedCompany), this.store.select(selectQuoteId), this.store.select(selectVersion)),
            concatMap(([{ versionId, request, dialogKey }, selectedCompany, quoteId, version]) =>
                this.translateService.get('ORDER_CONTENTS').pipe(
                    map(translation => {
                        const sub = version?.subscription;
                        if (sub?.plantPricePerSquareMeter && translation['PLANTS_SUBSCRIPTION']) {
                            const price = sub.plantPricePerSquareMeter.toLocaleString('sv-SE', { maximumFractionDigits: 2 });
                            const sqm = sub.plantSquareMetersEffective.toLocaleString('sv-SE', { maximumFractionDigits: 2 });
                            translation['PLANTS_SUBSCRIPTION'] = translation['PLANTS_SUBSCRIPTION'].replace('{price}', price).replace('{sqm}', sqm);
                        }
                        return { ...request, translations: JSON.stringify(translation) };
                    }),
                    catchError(() => of({ ...request, error: JSON.stringify({ error: "Default translation used due to an error" }) })),
                    switchMap(updatedRequest =>
                        this.quoteClient.createOrder(quoteId, versionId, selectedCompany.id, updatedRequest as any).pipe(
                            mergeMap(result => [QuoteActions.createOrderSuccess({
                                quote: result,
                                dialogKey,
                            })]),
                            catchError(error => of(QuoteActions.createOrderFailure({
                                error: normalizeError(error),
                                dialogKey,
                            }))),
                        ),
                    ),
                ),
            ),
        ),
    );

    // =========================================================================
    // NAVIGATION - Redirect after success
    // =========================================================================

    navigateToDuplicatedQuote$ = createEffect(
        () =>
            this.actions$.pipe(
                ofType(QuoteActions.duplicateQuoteSuccess),
                withLatestFrom(this.store.select(selectSelectedCompany)),
                tap(([{ quote }, selectedCompany]) => {
                    this.router.navigate([selectedCompany.id, "quote", quote.id]);
                }),
            ),
        { dispatch: false },
    );

    // =========================================================================
    // QUOTE INFO - Sales Credit (used in quote-info/sales-team component)
    // =========================================================================

    updateSalesCredit$ = createEffect(() =>
        this.actions$.pipe(
            ofType(QuoteActions.updateSalesCredit),
            withLatestFrom(this.store.select(selectSelectedCompany)),
            switchMap(([{ quoteId, request }, selectedCompany]) =>
                this.quoteSalesCreditClient.replace(selectedCompany.id, quoteId, request).pipe(
                    mergeMap(quote => [
                        QuoteActions.updateSalesCreditSuccess({
                            quote,
                            toastKey: 'QUOTE.TOAST.UPDATE_SUCCESS'
                        })
                    ]),
                    catchError(error => of(QuoteActions.updateSalesCreditFailure({
                        error: normalizeError(error),
                        toastKey: 'QUOTE.TOAST.UPDATE_ERROR'
                    }))),
                ),
            ),
        ),
    );

    // =========================================================================
    // DELIVERY - Delivery Address
    // =========================================================================

    createDeliveryAddress$ = createEffect(() =>
        this.actions$.pipe(
            ofType(QuoteActions.createDeliveryAddress),
            withLatestFrom(this.store.select(selectSelectedCompany)),
            switchMap(([{ quoteId, address }, selectedCompany]) =>
                this.deliveryAddressClient.create(quoteId, address, selectedCompany.id).pipe(
                    map(newAddress =>
                        QuoteActions.createDeliveryAddressSuccess({
                            address: newAddress,
                            toastKey: 'QUOTE.TOAST.DELIVERY_ADDRESS_CREATED'
                        })
                    ),
                    catchError(error => of(QuoteActions.createDeliveryAddressFailure({
                        error: normalizeError(error),
                        toastKey: 'QUOTE.TOAST.DELIVERY_ADDRESS_CREATE_ERROR'
                    }))),
                ),
            ),
        ),
    );

    // =========================================================================
    // NOTES - Comments
    // =========================================================================

    createComment$ = createEffect(() =>
        this.actions$.pipe(
            ofType(QuoteActions.createComment),
            withLatestFrom(this.store.select(selectSelectedCompany)),
            switchMap(([{ quoteId, request }, selectedCompany]) =>
                this.quoteClient.createComment(quoteId, request, selectedCompany.id).pipe(
                    mergeMap(quote => [
                        QuoteActions.createCommentSuccess({
                            quote,
                            toastKey: 'QUOTE.TOAST.COMMENT_CREATED'
                        })
                    ]),
                    catchError(error => of(QuoteActions.createCommentFailure({
                        error: normalizeError(error),
                        toastKey: 'QUOTE.TOAST.UPDATE_ERROR'
                    }))),
                ),
            ),
        ),
    );

    deleteComment$ = createEffect(() =>
        this.actions$.pipe(
            ofType(QuoteActions.deleteComment),
            withLatestFrom(this.store.select(selectSelectedCompany)),
            switchMap(([{ quoteId, commentId }, selectedCompany]) =>
                this.quoteClient.deleteComment(quoteId, commentId, selectedCompany.id).pipe(
                    mergeMap(quote => [
                        QuoteActions.deleteCommentSuccess({
                            quote,
                            toastKey: 'QUOTE.TOAST.COMMENT_DELETED'
                        })
                    ]),
                    catchError(error => of(QuoteActions.deleteCommentFailure({
                        error: normalizeError(error),
                        toastKey: 'QUOTE.TOAST.UPDATE_ERROR'
                    }))),
                ),
            ),
        ),
    );

    // =========================================================================
    // PREVIEW - PDF Download
    // =========================================================================

    downloadPdf$ = createEffect(() =>
        this.actions$.pipe(
            ofType(QuoteActions.downloadPdf),
            withLatestFrom(this.store.select(selectSelectedCompany)),
            concatMap(([{ request, dialogKey }, selectedCompany]) =>
                this.pdfClient.generatePdfToken(selectedCompany.id, request).pipe(
                    switchMap(tokenResponse =>
                        this.pdfClient.downloadPdf(request.type, tokenResponse.token).pipe(
                            mergeMap(result => {
                                const fileURL = URL.createObjectURL(result.data);
                                window.open(fileURL, "_blank", "noopener");
                                window.focus();
                                return [QuoteActions.downloadPdfSuccess({ dialogKey })];
                            }),
                        ),
                    ),
                    catchError(error => of(QuoteActions.downloadPdfFailure({
                        error: normalizeError(error),
                        dialogKey,
                        toastKey: 'QUOTE.PREVIEW.TOAST.DOWNLOAD_ERROR',
                    }))),
                ),
            ),
        ),
    );

    // =========================================================================
    // ATTIO SYNC
    // =========================================================================

    syncToAttio$ = createEffect(() =>
        this.actions$.pipe(
            ofType(QuoteActions.syncToAttio),
            withLatestFrom(this.store.select(selectSelectedCompany)),
            switchMap(([{ quoteId, versionId }, company]) =>
                this.attioClient.syncQuote(company.id, quoteId, versionId).pipe(
                    map(() => QuoteActions.syncToAttioSuccess({
                        toastKey: 'QUOTE.TOAST.SYNC_TO_ATTIO_SUCCESS',
                    })),
                    catchError(error => of(QuoteActions.syncToAttioFailure({
                        error: normalizeError(error),
                        toastKey: 'QUOTE.TOAST.SYNC_TO_ATTIO_ERROR',
                    }))),
                ),
            ),
        ),
    );

}
